import { useMemo } from 'react'

import { useUpdateCase } from '../hooks/useUpdateCase'
import { usePermissions } from '@/hooks/usePermissions'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Button,
  Flex,
  FormSection,
  MultiSelectInput,
  Text,
} from '@hivewatch/dark'
import type { ColorScheme, SelectOption } from '@hivewatch/dark'

import { ControlledSelect, ControlledTextInput } from '@/components/ui'
import { CaseQuery } from '@/graphql/generated/operations'
import {
  FacilityOptionWithAddressI,
  getDefaultCaseLabel,
  getFacilityOption,
  getUserOption,
} from '@/utils/forms/optionBuilders'
import { caseUpdateStatusOptions } from '@/utils/forms/optionConsts'

import { UpdateCaseFormInputsIProps } from '../types/types'
import { SelectedFacilityPill } from './SelectedFacilityPill'

interface UpdateCaseFormIProps {
  caseData: CaseQuery
  colorScheme?: ColorScheme
  onClose: () => void
}

export const UpdateCaseForm = ({
  caseData,
  colorScheme = 'light',
  onClose,
}: UpdateCaseFormIProps) => {
  const { t } = useTranslation()
  const prefix = colorScheme === 'light' ? 'light.' : ''
  const { shouldEnableCaseManagement } = usePermissions()

  const {
    onUpdateCase,
    setFacilityName,
    isLoading,
    isUsersLoading,
    facilityOptions,
    userOptions,
  } = useUpdateCase({ onClose, caseId: caseData?.case?.id })

  // MultiSelectInput's subtitle/caption are plain presentation strings, so
  // the "matched by address" cue is translated here rather than baked into
  // getIncidentFacilityOptionsWithAddress (which stays i18n-agnostic).
  const facilityOptionsWithCue = useMemo(
    () =>
      (facilityOptions as FacilityOptionWithAddressI[]).map((option) => ({
        ...option,
        caption:
          option.matchedField === 'address'
            ? t('common:facility_matched_by_address')
            : undefined,
      })),
    [facilityOptions, t]
  )

  const { handleSubmit, control, errors, formState } =
    useForm<UpdateCaseFormInputsIProps>({
      mode: 'onChange',
      reValidateMode: 'onChange',
      defaultValues: {
        name: caseData?.case?.name,
        facilities: caseData?.case?.facilities?.map(getFacilityOption) || [],
        owner: caseData?.case?.owner
          ? getUserOption(caseData.case.owner)
          : null,
        status: {
          label: getDefaultCaseLabel(caseData?.case?.status),
          value: caseData?.case?.status,
        },
      },
    })
  const { isValid, isDirty } = formState

  return (
    <Box display='flex' flexDirection='column' h='100%'>
      <Flex direction='column' gap='16px'>
        <FormSection
          colorScheme={colorScheme}
          title={t('cases:crud_case_name_label')}
        >
          <ControlledTextInput
            colorScheme={colorScheme}
            control={control}
            disabled={isLoading}
            errors={errors}
            inputProps={{ 'aria-label': t('cases:crud_case_name_label') }}
            name='name'
            placeholder={t('cases:crud_case_name_placeholder')}
            rules={{ required: t('cases:crud_case_name_required') }}
            width='100%'
          />
        </FormSection>

        <FormSection
          colorScheme={colorScheme}
          title={t('cases:crud_facilities_label')}
        >
          <Controller
            control={control}
            defaultValue={[]}
            name='facilities'
            render={({ value, onChange }) => {
              const selected = (value ?? []) as SelectOption[]
              const selectedValues = selected.map((facility) => facility.value)

              const toggleFacility = (facilityValue: string) => {
                if (selectedValues.includes(facilityValue)) {
                  onChange(
                    selected.filter(
                      (facility) => facility.value !== facilityValue
                    )
                  )
                  return
                }
                // The clicked option must be currently rendered in the search
                // results to have been clickable at all, so it's always found
                // in facilityOptionsWithCue here.
                const option = facilityOptionsWithCue.find(
                  (facility) => facility.value === facilityValue
                )
                if (option) onChange([...selected, option])
              }

              return (
                <Box width='100%'>
                  <MultiSelectInput
                    aria-label={t('cases:crud_facilities_label')}
                    colorScheme={colorScheme}
                    // The facility search is server-side and also matches by
                    // address, so an address-only match's label may not
                    // contain the typed query — 'internal' mode would
                    // re-filter it away.
                    filterMode='external'
                    maxDropdownHeight='200px'
                    maxResults={10}
                    noMatchLabel={t('cases:crud_no_facilities_found')}
                    onClear={() => onChange([])}
                    onQueryChange={setFacilityName}
                    onRemove={toggleFacility}
                    onToggleOption={toggleFacility}
                    options={facilityOptionsWithCue}
                    placeholder={t('cases:crud_facilities_placeholder')}
                    renderSelectedItem={({ item, onRemove }) => (
                      <SelectedFacilityPill
                        colorScheme={colorScheme}
                        label={item.label}
                        onRemove={onRemove}
                      />
                    )}
                    searchPlaceholder={t('cases:crud_search_facilities')}
                    searchable
                    selected={selectedValues}
                    selectedOptions={selected}
                    usePortal
                  />
                  {errors.facilities && (
                    <Text
                      color={`${prefix}status.error-text`}
                      mt='6px'
                      textAlign='left'
                      textStyle='small-caption'
                    >
                      {t('cases:crud_facilities_required')}
                    </Text>
                  )}
                </Box>
              )
            }}
            rules={{ validate: (value) => (value?.length ?? 0) > 0 }}
          />
        </FormSection>

        <FormSection
          colorScheme={colorScheme}
          title={t('cases:crud_owner_label')}
        >
          <Box width='100%'>
            <ControlledSelect
              aria-label={t('cases:crud_owner_label')}
              colorScheme={colorScheme}
              control={control}
              isLoading={isUsersLoading}
              name='owner'
              options={userOptions}
              placeholder={t('cases:crud_owner_placeholder')}
              rules={{ required: true }}
            />
            {errors.owner && (
              <Text
                color={`${prefix}status.error-text`}
                mt='6px'
                textAlign='left'
                textStyle='small-caption'
              >
                {t('cases:crud_owner_required')}
              </Text>
            )}
          </Box>
        </FormSection>

        <FormSection
          colorScheme={colorScheme}
          title={t('cases:crud_status_label')}
        >
          <Box width='100%'>
            <ControlledSelect
              aria-label={t('cases:crud_status_label')}
              colorScheme={colorScheme}
              control={control}
              name='status'
              options={caseUpdateStatusOptions}
              rules={{ required: true }}
            />
            {errors.status && (
              <Text
                color={`${prefix}status.error-text`}
                mt='6px'
                textAlign='left'
                textStyle='small-caption'
              >
                {t('cases:crud_status_required')}
              </Text>
            )}
          </Box>
        </FormSection>
      </Flex>

      <Flex gap='12px' justifyContent='flex-end' mt='auto' pt='16px'>
        <Button
          colorScheme={colorScheme}
          onClick={onClose}
          size='sm'
          variant='secondary'
        >
          {t('common:cancel')}
        </Button>
        <Button
          colorScheme={colorScheme}
          disabled={!shouldEnableCaseManagement || !isValid || !isDirty}
          isLoading={isLoading}
          onClick={handleSubmit(onUpdateCase)}
          size='sm'
          variant='primary'
        >
          {t('cases:crud_update_case')}
        </Button>
      </Flex>
    </Box>
  )
}
